import * as m from '@staiter/i18n';
import { MINUS_SIGN } from '@staiter/i18n/format';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LineChart as LineIcon, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EmptyState, ExportMenu, PageHeader } from '~/components/data';
import { DateRangePicker, PeriodPresetChips } from '~/components/primitives';
import { fetchCashFlow } from '~/lib/api/finance';
import { formatMoney } from '~/lib/format';
import shared from './_auth._shared.module.css';
import styles from './_auth.finance.cash-flow.module.css';

/**
 * S15-FE.2 — Cash-flow report page (actual stacked + forecast buckets).
 */

export const Route = createFileRoute('/_auth/finance/cash-flow')({
  component: FinanceCashFlowPage,
});

const BUCKET_LABELS: Record<string, () => string> = {
  overdue: m.cash_flow_bucket_overdue,
  '0_30': m.cash_flow_bucket_0_30,
  '31_60': m.cash_flow_bucket_31_60,
  '61_90': m.cash_flow_bucket_61_90,
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
// S-FIX-6: default — YTD (а не "первое число текущего месяца"), иначе
// первый раз открыв пустой период (e.g. начало месяца) пользователь видит
// нули и не понимает что данные есть. YTD показывает реальный объём сразу.
function firstOfYear(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-01-01`;
}

function FinanceCashFlowPage(): JSX.Element {
  const [dateFrom, setDateFrom] = useState(firstOfYear());
  const [dateTo, setDateTo] = useState(today());
  const query = useQuery({
    queryKey: ['finance', 'cash-flow', dateFrom, dateTo],
    queryFn: () => fetchCashFlow({ date_from: dateFrom, date_to: dateTo }),
    enabled: Boolean(dateFrom && dateTo),
  });
  const data = query.data;

  const hasData = useMemo(
    () => !!data && (data.actual.length > 0 || data.forecast.length > 0),
    [data],
  );

  return (
    <div>
      <PageHeader
        title={m.phase2_finance_cash_flow_title()}
        actions={
          // FE-160 Sprint N+ batch 3: Excel export CTA.
          <ExportMenu
            resource="finance-cash-flow"
            filters={{ date_from: dateFrom, date_to: dateTo }}
          />
        }
      />

      <div className={styles.filterRow}>
        {/* S-FIX-5 fix: native date input → DateRangePicker. */}
        <DateRangePicker
          label={m.cogs_filter_date_from()}
          value={{ from: dateFrom || null, to: dateTo || null }}
          onChange={(r) => {
            setDateFrom(r.from ?? '');
            setDateTo(r.to ?? '');
          }}
        />
        {/* FE-279 Pass 27: shared preset chips. */}
        <PeriodPresetChips
          onChange={(r) => {
            setDateFrom(r.from);
            setDateTo(r.to);
          }}
        />
      </div>

      {!hasData && !query.isLoading ? (
        <EmptyState title={m.cash_flow_actual_title()} icon={<LineIcon size={32} />} />
      ) : data ? (
        <>
          {/* S-FIX-6-FE.2.1: Recharts BarChart. Time-series requires BE-side
              day/week/month bucketing (S-FIX-6-BE.5); пока — агрегаты по
              counterparty_kind (inflow/outflow per kind), что уже информативнее
              голого "plat text". */}
          <CashFlowChart data={data} />
          <div className={styles.cardsGrid}>
            <div className={shared.elevatedCard}>
              <h2 className={shared.h2NoMargin}>{m.cash_flow_actual_title()}</h2>
              <div>
                <div>
                  {m.payment_direction_inflow()}:{' '}
                  <strong data-testid="cf-actual-inflow">
                    {formatMoney(data.actual_totals.inflow)}
                  </strong>
                </div>
                <div>
                  {m.payment_direction_outflow()}:{' '}
                  <strong data-testid="cf-actual-outflow">
                    {formatMoney(data.actual_totals.outflow)}
                  </strong>
                </div>
                <div>
                  {m.cash_flow_net_expected()}:{' '}
                  <strong data-testid="cf-actual-net">{formatMoney(data.actual_totals.net)}</strong>
                </div>
              </div>
            </div>
            <div
              className={[shared.elevatedCard, styles.forecastCard].filter(Boolean).join(' ')}
              data-kind="forecast"
            >
              <h2 className={[shared.h2NoMargin, styles.forecastTitle].join(' ')}>
                <Sparkles size={14} aria-hidden="true" className={styles.agentIcon} />
                {m.cash_flow_forecast_title()}
                <span className={styles.agentBadge}>{m.cash_flow_forecast_badge_ai()}</span>
              </h2>
              {data.forecast.length === 0 ? (
                <div>—</div>
              ) : (
                <div>
                  {data.forecast.map((row, idx) => (
                    <div key={`${row.bucket}-${row.direction}-${idx}`}>
                      {(BUCKET_LABELS[row.bucket] ?? (() => row.bucket))()}
                      {' / '}
                      {row.direction === 'inflow'
                        ? m.payment_direction_inflow()
                        : m.payment_direction_outflow()}
                      : <strong>{formatMoney(row.expected_amount)}</strong>
                    </div>
                  ))}
                  <div className={styles.forecastTotal}>
                    {m.cash_flow_net_expected()}:{' '}
                    <strong data-testid="cf-forecast-net">
                      {formatMoney(data.forecast_totals.net_expected)}
                    </strong>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* U-5 (F8) — drill фактического cash-flow по статье/группе/проекту (Adesk cf_article/…). */}
          <CashFlowDrill rows={data.by_article ?? []} />
        </>
      ) : null}
    </div>
  );
}

/** U-5 — таблица разбивки фактических платежей по cf_article/cf_group/project_tag (KZT). */
function CashFlowDrill({
  rows,
}: {
  rows: Array<{
    article: string | null;
    group: string | null;
    project_tag: string | null;
    inflow_kzt: string;
    outflow_kzt: string;
    net_kzt: string;
    payment_count: number;
  }>;
}): JSX.Element {
  const money = (v: string) => formatMoney({ amount: v, currency: 'KZT' });
  // Итоговая строка (design-review UX MINOR): замыкает reconciliation drill ↔ агрегат.
  const totals = rows.reduce(
    (a, r) => ({
      inflow: a.inflow + Number(r.inflow_kzt),
      outflow: a.outflow + Number(r.outflow_kzt),
      net: a.net + Number(r.net_kzt),
    }),
    { inflow: 0, outflow: 0, net: 0 },
  );
  return (
    <div className={shared.elevatedCard} style={{ marginTop: 16, maxWidth: 1200 }}>
      <h2 className={shared.h2NoMargin}>{m.cf_drill_title()}</h2>
      {rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>{m.cf_drill_empty()}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className={styles.drillTable}>
            <thead>
              <tr>
                <th>{m.cf_drill_col_article()}</th>
                <th>{m.cf_drill_col_group()}</th>
                <th>{m.cf_drill_col_project()}</th>
                <th className={styles.num}>{m.payment_direction_inflow()}</th>
                <th className={styles.num}>{m.payment_direction_outflow()}</th>
                <th className={styles.num}>{m.cf_drill_col_net()}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.article ?? '∅'}-${r.group ?? ''}-${r.project_tag ?? ''}-${i}`}>
                  <td>
                    {r.article ?? (
                      <span style={{ color: 'var(--text-muted)' }}>{m.cf_drill_no_article()}</span>
                    )}
                  </td>
                  <td>{r.group ?? '—'}</td>
                  <td>{r.project_tag ?? '—'}</td>
                  <td className={styles.num} data-testid="cf-drill-inflow">
                    {money(r.inflow_kzt)}
                  </td>
                  <td className={styles.num}>{money(r.outflow_kzt)}</td>
                  <td
                    className={styles.num}
                    style={{ color: Number(r.net_kzt) < 0 ? 'var(--danger)' : undefined }}
                  >
                    {money(r.net_kzt)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={styles.drillTotal}>
                <td>{m.cf_drill_total()}</td>
                <td />
                <td />
                <td className={styles.num}>{money(totals.inflow.toFixed(2))}</td>
                <td className={styles.num}>{money(totals.outflow.toFixed(2))}</td>
                <td
                  className={styles.num}
                  style={{ color: totals.net < 0 ? 'var(--danger)' : undefined }}
                >
                  {money(totals.net.toFixed(2))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/** S-FIX-6-FE.2.1: bar chart inflow/outflow per counterparty_kind. */
type CashFlowChartProps = {
  data: {
    actual: {
      direction: 'inflow' | 'outflow';
      counterparty_kind: string;
      amount: { amount: string };
    }[];
  };
};
// FE-P2-3 + P2-4 (QA 2026-06-04): localized x-axis category labels + compact y-axis.
function kindLabel(kind: string): string {
  switch (kind) {
    case 'customer':
      return m.cp_kind_customer();
    case 'supplier':
      return m.cp_kind_supplier();
    case 'employee':
      return m.cp_kind_employee();
    case 'other':
      return m.cp_kind_other();
    default:
      return kind;
  }
}

function formatYAxisCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? MINUS_SIGN : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1).replace('.', ',')} млрд`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1).replace('.', ',')} млн`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)} тыс`;
  return `${sign}${abs.toFixed(0)}`;
}

function CashFlowChart({ data }: CashFlowChartProps): JSX.Element | null {
  const chartData = useMemo(() => {
    const byKind = new Map<
      string,
      { kind: string; kindLabel: string; inflow: number; outflow: number }
    >();
    for (const row of data.actual) {
      const key = row.counterparty_kind;
      const acc = byKind.get(key) ?? {
        kind: key,
        kindLabel: kindLabel(key),
        inflow: 0,
        outflow: 0,
      };
      const amt = Number(row.amount.amount);
      if (row.direction === 'inflow') acc.inflow += amt;
      else acc.outflow += amt;
      byKind.set(key, acc);
    }
    return [...byKind.values()];
  }, [data]);
  if (chartData.length === 0) return null;
  return (
    <div className={styles.chartCard}>
      <h2 className={styles.chartCardTitle}>{m.cash_flow_actual_title()}</h2>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis dataKey="kindLabel" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={formatYAxisCompact} />
          <Tooltip />
          <Legend />
          {/* FE-158 Sprint N+ batch 3: Calm UI muted bar fills через OKLCH
              alpha-blended tokens (var(--ok-bg-30 / --danger-bg-30) вместо
              насыщенных primary colors). */}
          <Bar
            dataKey="inflow"
            fill="var(--ok-bg-30, var(--color-success))"
            stroke="var(--color-ok, var(--color-success))"
            strokeWidth={1}
            name={m.payment_direction_inflow()}
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="outflow"
            fill="var(--danger-bg-30, var(--color-danger))"
            stroke="var(--color-danger)"
            strokeWidth={1}
            name={m.payment_direction_outflow()}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
